import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Get vendor balance
export const getVendorBalance = createAsyncThunk(
  "vendorPayouts/getBalance",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendor/payouts/balance`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch balance" }
      );
    }
  }
);

// Get all payouts
export const getVendorPayouts = createAsyncThunk(
  "vendorPayouts/getAll",
  async ({ status }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);

      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendor/payouts?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch payouts" }
      );
    }
  }
);

// Request payout
export const requestPayout = createAsyncThunk(
  "vendorPayouts/request",
  async ({ amount, paymentMethod, vendorNotes }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_BACKEND_URL}/api/vendor/payouts/request`,
        { amount, paymentMethod, vendorNotes },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to request payout" }
      );
    }
  }
);

// Admin: Get all payouts
export const getAllPayouts = createAsyncThunk(
  "vendorPayouts/getAllAdmin",
  async ({ status, vendor }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (vendor) params.append("vendor", vendor);

      const response = await axios.get(
        `${import.meta.env.VITE_BACKEND_URL}/api/admin/payouts/all?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to fetch payouts" }
      );
    }
  }
);

// Admin: Approve payout
export const approvePayout = createAsyncThunk(
  "vendorPayouts/approve",
  async (payoutId, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/api/admin/payouts/${payoutId}/approve`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to approve payout" }
      );
    }
  }
);

// Admin: Complete payout
export const completePayout = createAsyncThunk(
  "vendorPayouts/complete",
  async ({ payoutId, transactionId, adminNotes }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/api/admin/payouts/${payoutId}/complete`,
        { transactionId, adminNotes },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to complete payout" }
      );
    }
  }
);

// Admin: Reject payout
export const rejectPayout = createAsyncThunk(
  "vendorPayouts/reject",
  async ({ payoutId, reason }, { rejectWithValue }) => {
    try {
      const response = await axios.put(
        `${import.meta.env.VITE_BACKEND_URL}/api/admin/payouts/${payoutId}/reject`,
        { reason },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            "Content-Type": "application/json",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: "Failed to reject payout" }
      );
    }
  }
);

const vendorPayoutSlice = createSlice({
  name: "vendorPayouts",
  initialState: {
    balance: null,
    payouts: [],
    adminPayouts: [], // For admin view
    loading: false,
    error: null,
    requestLoading: false,
    actionLoading: false,
  },
  reducers: {
    clearPayoutError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Balance
      .addCase(getVendorBalance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVendorBalance.fulfilled, (state, action) => {
        state.loading = false;
        state.balance = action.payload;
        state.error = null;
      })
      .addCase(getVendorBalance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch balance";
      })

      // Get Payouts
      .addCase(getVendorPayouts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getVendorPayouts.fulfilled, (state, action) => {
        state.loading = false;
        state.payouts = action.payload.payouts;
        state.error = null;
      })
      .addCase(getVendorPayouts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch payouts";
      })

      // Request Payout
      .addCase(requestPayout.pending, (state) => {
        state.requestLoading = true;
        state.error = null;
      })
      .addCase(requestPayout.fulfilled, (state, action) => {
        state.requestLoading = false;
        state.payouts.unshift(action.payload.payout);
        state.error = null;
      })
      .addCase(requestPayout.rejected, (state, action) => {
        state.requestLoading = false;
        state.error = action.payload?.message || "Failed to request payout";
      })

      // Get All Payouts (Admin)
      .addCase(getAllPayouts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllPayouts.fulfilled, (state, action) => {
        state.loading = false;
        state.adminPayouts = action.payload.payouts;
        state.error = null;
      })
      .addCase(getAllPayouts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch payouts";
      })

      // Approve Payout
      .addCase(approvePayout.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(approvePayout.fulfilled, (state, action) => {
        state.actionLoading = false;
        const index = state.adminPayouts.findIndex(
          (p) => p._id === action.payload.payout._id
        );
        if (index !== -1) {
          state.adminPayouts[index] = action.payload.payout;
        }
        state.error = null;
      })
      .addCase(approvePayout.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.message || "Failed to approve payout";
      })

      // Complete Payout
      .addCase(completePayout.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(completePayout.fulfilled, (state, action) => {
        state.actionLoading = false;
        const index = state.adminPayouts.findIndex(
          (p) => p._id === action.payload.payout._id
        );
        if (index !== -1) {
          state.adminPayouts[index] = action.payload.payout;
        }
        state.error = null;
      })
      .addCase(completePayout.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.message || "Failed to complete payout";
      })

      // Reject Payout
      .addCase(rejectPayout.pending, (state) => {
        state.actionLoading = true;
        state.error = null;
      })
      .addCase(rejectPayout.fulfilled, (state, action) => {
        state.actionLoading = false;
        const index = state.adminPayouts.findIndex(
          (p) => p._id === action.payload.payout._id
        );
        if (index !== -1) {
          state.adminPayouts[index] = action.payload.payout;
        }
        state.error = null;
      })
      .addCase(rejectPayout.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload?.message || "Failed to reject payout";
      });
  },
});

export const { clearPayoutError } = vendorPayoutSlice.actions;
export default vendorPayoutSlice.reducer;
